import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import apiClient from '../api/axios';
import { toast } from 'react-toastify';

// Assuming UserOrgRole enum values are available or can be hardcoded
const userOrgRoles = [
    { value: 'owner', label: 'Владелец' },
    { value: 'admin', label: 'Администратор' },
    { value: 'editor', label: 'Редактор' },
    { value: 'member', label: 'Участник' },
];

const EditUserModal = ({ show, handleClose, user, onUserUpdated }) => {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [isEmailConfirmed, setIsEmailConfirmed] = useState(false);
    const [organizationId, setOrganizationId] = useState('');
    const [roleInOrganization, setRoleInOrganization] = useState('');

    const [organizations, setOrganizations] = useState([]);
    const [loadingOrgs, setLoadingOrgs] = useState(true);
    const [orgsError, setOrgsError] = useState(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (user) {
            setEmail(user.email);
            setName(user.name || '');
            setIsEmailConfirmed(user.is_email_confirmed);
            setOrganizationId(user.organization_id || '');
            setRoleInOrganization(user.role_in_organization || '');
        }
    }, [user]);

    useEffect(() => {
        const fetchOrganizations = async () => {
            try {
                const response = await apiClient.get('/admin/organizations/');
                setOrganizations(response.data.organizations);
            } catch (err) {
                setOrgsError('Не удалось загрузить список организаций.');
                console.error('Error fetching organizations:', err);
            } finally {
                setLoadingOrgs(false);
            }
        };

        if (show) {
            fetchOrganizations();
        }
    }, [show]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const payload = {
                email: email,
                name: name || null,
                is_email_confirmed: isEmailConfirmed,
            };

            // Only include organization_id and role_in_organization if they are set
            if (organizationId) {
                payload.organization_id = parseInt(organizationId);
                payload.role_in_organization = roleInOrganization || 'member';
            } else {
                // If organization is unset, ensure role is also unset
                payload.organization_id = null;
                payload.role_in_organization = null;
            }

            const response = await apiClient.put(`/admin/users/${user.id}`, payload);
            toast.success('Данные пользователя успешно обновлены!');
            onUserUpdated(response.data); // Pass updated user data back
            handleClose();
        } catch (err) {
            let errorMessage = 'Ошибка при обновлении пользователя.';
            if (err.response && err.response.data && err.response.data.detail) {
                errorMessage = err.response.data.detail;
            }
            setError(errorMessage);
            toast.error(errorMessage);
            console.error('Update user error:', err);
        } finally {
            setLoading(false);
        }
    };

    if (!user) return null; // Don't render if no user is passed

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Редактировать пользователя: {user.email}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {orgsError && <Alert variant="warning">{orgsError}</Alert>}
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3" controlId="editUserEmail">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="email"
                            placeholder="Введите email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="editUserName">
                        <Form.Label>Имя</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Введите имя"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="editUserEmailConfirmed">
                        <Form.Check
                            type="checkbox"
                            label="Email подтвержден"
                            checked={isEmailConfirmed}
                            onChange={(e) => setIsEmailConfirmed(e.target.checked)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="editUserOrganization">
                        <Form.Label>Организация</Form.Label>
                        <Form.Control
                            as="select"
                            value={organizationId}
                            onChange={(e) => {
                                setOrganizationId(e.target.value);
                                if (!e.target.value) { // Reset role if no organization selected
                                    setRoleInOrganization('');
                                }
                            }}
                            disabled={loadingOrgs}
                        >
                            <option value="">Без организации</option>
                            {organizations.map(org => (
                                <option key={org.id} value={org.id}>{org.name}</option>
                            ))}
                        </Form.Control>
                    </Form.Group>

                    {organizationId && (
                        <Form.Group className="mb-3" controlId="editUserRoleInOrganization">
                            <Form.Label>Роль в организации</Form.Label>
                            <Form.Control
                                as="select"
                                value={roleInOrganization}
                                onChange={(e) => setRoleInOrganization(e.target.value)}
                                required={!!organizationId}
                            >
                                <option value="">Выберите роль</option>
                                {userOrgRoles.map(role => (
                                    <option key={role.value} value={role.value}>{role.label}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    )}

                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? 'Сохранение...' : 'Сохранить изменения'}
                    </Button>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default EditUserModal;
